import React, { useMemo, useState } from "react";
import {
    CircleDot,
    Plus,
    Search,
    Edit,
    Trash2,
    CheckCircle2,
    XCircle,
    RotateCcw,
    ArrowUp,
    ArrowDown,
} from "lucide-react";
import "../../styles/QuoteStatus.css";

const initialQuoteStatuses = [
    {
        id: 1,
        name: "New",
        code: "NEW",
        description: "New quote received from the customer.",
        color: "#2563eb",
        order: 1,
        status: "ACTIVE",
    },
    {
        id: 2,
        name: "Contacted",
        code: "CONTACTED",
        description: "Customer has been contacted regarding the quote.",
        color: "#0891b2",
        order: 2,
        status: "ACTIVE",
    },
    {
        id: 3,
        name: "Quotation Sent",
        code: "QUOTATION_SENT",
        description: "Quotation has been prepared and sent to the customer.",
        color: "#7c3aed",
        order: 3,
        status: "ACTIVE",
    },
    {
        id: 4,
        name: "In Progress",
        code: "IN_PROGRESS",
        description: "Customer has accepted the quotation and move is in progress.",
        color: "#d97706",
        order: 4,
        status: "ACTIVE",
    },
    {
        id: 5,
        name: "Completed",
        code: "COMPLETED",
        description: "Moving service has been completed successfully.",
        color: "#16a34a",
        order: 5,
        status: "ACTIVE",
    },
    {
        id: 6,
        name: "Cancelled",
        code: "CANCELLED",
        description: "Quote or moving request has been cancelled.",
        color: "#dc2626",
        order: 6,
        status: "ACTIVE",
    },
    {
        id: 7,
        name: "Expired",
        code: "EXPIRED",
        description: "Quotation validity period has expired.",
        color: "#64748b",
        order: 7,
        status: "ACTIVE",
    },
    {
        id: 8,
        name: "On Hold",
        code: "ON_HOLD",
        description: "Quote is temporarily kept on hold.",
        color: "#ca8a04",
        order: 8,
        status: "INACTIVE",
    },
];

export default function QuoteStatus() {
    const [quoteStatuses, setQuoteStatuses] = useState(
        initialQuoteStatuses
    );

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const [page, setPage] = useState(1);
    const rowsPerPage = 6;

    const filteredStatuses = useMemo(() => {
        return quoteStatuses
            .filter((item) => {
                const searchText = search.toLowerCase();

                const matchesSearch =
                    item.name.toLowerCase().includes(searchText) ||
                    item.code.toLowerCase().includes(searchText) ||
                    item.description.toLowerCase().includes(searchText);

                const matchesStatus =
                    !statusFilter ||
                    item.status === statusFilter;

                return matchesSearch && matchesStatus;
            })
            .sort((a, b) => a.order - b.order);
    }, [quoteStatuses, search, statusFilter]);

    const totalPages = Math.ceil(
        filteredStatuses.length / rowsPerPage
    );

    const paginatedStatuses = filteredStatuses.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const totalStatuses = quoteStatuses.length;

    const activeStatuses = quoteStatuses.filter(
        (item) => item.status === "ACTIVE"
    ).length;

    const inactiveStatuses = quoteStatuses.filter(
        (item) => item.status === "INACTIVE"
    ).length;

    const handleSearch = (value) => {
        setSearch(value);
        setPage(1);
    };

    const handleStatusFilter = (value) => {
        setStatusFilter(value);
        setPage(1);
    };

    const clearFilters = () => {
        setSearch("");
        setStatusFilter("");
        setPage(1);
    };

    const handleAdd = () => {
        alert("Add Quote Status form will be connected here.");
    };

    const handleEdit = (item) => {
        alert(`Edit Quote Status: ${item.name}`);
    };

    const handleDelete = (id) => {
        const item = quoteStatuses.find(
            (status) => status.id === id
        );

        if (!item) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete "${item.name}"?`
        );

        if (!confirmed) return;

        setQuoteStatuses((prev) =>
            prev.filter((status) => status.id !== id)
        );
    };

    const toggleStatus = (id) => {
        setQuoteStatuses((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        status:
                            item.status === "ACTIVE"
                                ? "INACTIVE"
                                : "ACTIVE",
                    }
                    : item
            )
        );
    };

    const moveStatus = (id, direction) => {
        setQuoteStatuses((prev) => {
            const sorted = [...prev].sort(
                (a, b) => a.order - b.order
            );

            const currentIndex = sorted.findIndex(
                (item) => item.id === id
            );

            const targetIndex =
                direction === "up"
                    ? currentIndex - 1
                    : currentIndex + 1;

            if (
                currentIndex < 0 ||
                targetIndex < 0 ||
                targetIndex >= sorted.length
            ) {
                return prev;
            }

            const current = sorted[currentIndex];
            const target = sorted[targetIndex];

            const currentOrder = current.order;

            current.order = target.order;
            target.order = currentOrder;

            return sorted;
        });
    };

    return (
        <div className="quote-status-page">

            {/* Header */}
            <div className="quote-status-header">

                <div className="quote-status-title">

                    <div className="quote-status-title-icon">
                        <CircleDot size={24} />
                    </div>

                    <div>
                        <h1>Quote Status</h1>
                        <p>
                            Manage quote workflow statuses and display order.
                        </p>
                    </div>

                </div>

                <button
                    className="quote-status-primary-btn"
                    onClick={handleAdd}
                >
                    <Plus size={18} />
                    Add Status
                </button>

            </div>

            {/* Summary */}
            <div className="quote-status-summary">

                <div className="quote-status-summary-card">

                    <div className="quote-status-summary-icon total">
                        <CircleDot size={21} />
                    </div>

                    <div>
                        <span>Total Statuses</span>
                        <strong>{totalStatuses}</strong>
                    </div>

                </div>

                <div className="quote-status-summary-card">

                    <div className="quote-status-summary-icon active">
                        <CheckCircle2 size={21} />
                    </div>

                    <div>
                        <span>Active Statuses</span>
                        <strong>{activeStatuses}</strong>
                    </div>

                </div>

                <div className="quote-status-summary-card">

                    <div className="quote-status-summary-icon workflow">
                        <ArrowUp size={21} />
                    </div>

                    <div>
                        <span>Workflow Steps</span>
                        <strong>{activeStatuses}</strong>
                    </div>

                </div>

                <div className="quote-status-summary-card">

                    <div className="quote-status-summary-icon inactive">
                        <XCircle size={21} />
                    </div>

                    <div>
                        <span>Inactive Statuses</span>
                        <strong>{inactiveStatuses}</strong>
                    </div>

                </div>

            </div>

            {/* Filters */}
            <div className="quote-status-filter-card">

                <div className="quote-status-search">

                    <Search size={18} />

                    <input
                        type="text"
                        placeholder="Search status name, code or description..."
                        value={search}
                        onChange={(e) =>
                            handleSearch(e.target.value)
                        }
                    />

                </div>

                <select
                    value={statusFilter}
                    onChange={(e) =>
                        handleStatusFilter(e.target.value)
                    }
                >
                    <option value="">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                </select>

                <button
                    className="quote-status-clear-btn"
                    onClick={clearFilters}
                >
                    <RotateCcw size={16} />
                    Clear
                </button>

            </div>

            {/* Table */}
            <div className="quote-status-table-card">

                <div className="quote-status-table-wrapper">

                    <table className="quote-status-table">

                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Status</th>
                                <th>Code</th>
                                <th>Description</th>
                                <th>Color</th>
                                <th>Order</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>

                            {paginatedStatuses.length > 0 ? (
                                paginatedStatuses.map(
                                    (item, index) => (
                                        <tr key={item.id}>

                                            <td>
                                                {(page - 1) *
                                                    rowsPerPage +
                                                    index +
                                                    1}
                                            </td>

                                            <td>
                                                <div className="quote-status-name-cell">

                                                    <span
                                                        className="quote-status-color-dot"
                                                        style={{
                                                            backgroundColor:
                                                                item.color,
                                                        }}
                                                    />

                                                    <strong>
                                                        {item.name}
                                                    </strong>

                                                </div>
                                            </td>

                                            <td>
                                                <span className="quote-status-code">
                                                    {item.code}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="quote-status-description">
                                                    {item.description}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="quote-status-color-cell">

                                                    <span
                                                        className="quote-status-color-preview"
                                                        style={{
                                                            backgroundColor:
                                                                item.color,
                                                        }}
                                                    />

                                                    <span>
                                                        {item.color}
                                                    </span>

                                                </div>
                                            </td>

                                            <td>
                                                <div className="quote-status-order">

                                                    <button
                                                        disabled={
                                                            item.order ===
                                                            1
                                                        }
                                                        onClick={() =>
                                                            moveStatus(
                                                                item.id,
                                                                "up"
                                                            )
                                                        }
                                                        title="Move Up"
                                                    >
                                                        <ArrowUp
                                                            size={14}
                                                        />
                                                    </button>

                                                    <span>
                                                        {item.order}
                                                    </span>

                                                    <button
                                                        disabled={
                                                            item.order ===
                                                            quoteStatuses.length
                                                        }
                                                        onClick={() =>
                                                            moveStatus(
                                                                item.id,
                                                                "down"
                                                            )
                                                        }
                                                        title="Move Down"
                                                    >
                                                        <ArrowDown
                                                            size={14}
                                                        />
                                                    </button>

                                                </div>
                                            </td>

                                            <td>
                                                <button
                                                    className={`quote-status-badge ${
                                                        item.status ===
                                                        "ACTIVE"
                                                            ? "active"
                                                            : "inactive"
                                                    }`}
                                                    onClick={() =>
                                                        toggleStatus(
                                                            item.id
                                                        )
                                                    }
                                                >
                                                    {item.status ===
                                                    "ACTIVE"
                                                        ? "Active"
                                                        : "Inactive"}
                                                </button>
                                            </td>

                                            <td>
                                                <div className="quote-status-actions">

                                                    <button
                                                        className="quote-status-action edit"
                                                        onClick={() =>
                                                            handleEdit(
                                                                item
                                                            )
                                                        }
                                                        title="Edit"
                                                    >
                                                        <Edit
                                                            size={16}
                                                        />
                                                    </button>

                                                    <button
                                                        className="quote-status-action delete"
                                                        onClick={() =>
                                                            handleDelete(
                                                                item.id
                                                            )
                                                        }
                                                        title="Delete"
                                                    >
                                                        <Trash2
                                                            size={16}
                                                        />
                                                    </button>

                                                </div>
                                            </td>

                                        </tr>
                                    )
                                )
                            ) : (
                                <tr>
                                    <td
                                        colSpan="8"
                                        className="quote-status-empty"
                                    >
                                        No quote statuses found.
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>

                {/* Mobile Cards */}
                <div className="quote-status-mobile-list">

                    {paginatedStatuses.length > 0 ? (
                        paginatedStatuses.map(
                            (item, index) => (
                                <div
                                    className="quote-status-mobile-card"
                                    key={item.id}
                                >

                                    <div className="quote-status-mobile-top">

                                        <div className="quote-status-mobile-title">

                                            <span
                                                className="quote-status-color-dot"
                                                style={{
                                                    backgroundColor:
                                                        item.color,
                                                }}
                                            />

                                            <div>
                                                <strong>
                                                    {item.name}
                                                </strong>

                                                <span>
                                                    {item.code}
                                                </span>
                                            </div>

                                        </div>

                                        <button
                                            className={`quote-status-badge ${
                                                item.status ===
                                                "ACTIVE"
                                                    ? "active"
                                                    : "inactive"
                                            }`}
                                            onClick={() =>
                                                toggleStatus(
                                                    item.id
                                                )
                                            }
                                        >
                                            {item.status ===
                                            "ACTIVE"
                                                ? "Active"
                                                : "Inactive"}
                                        </button>

                                    </div>

                                    <p className="quote-status-mobile-description">
                                        {item.description}
                                    </p>

                                    <div className="quote-status-mobile-details">

                                        <div>
                                            <span>Code</span>
                                            <strong>
                                                {item.code}
                                            </strong>
                                        </div>

                                        <div>
                                            <span>Order</span>
                                            <strong>
                                                {item.order}
                                            </strong>
                                        </div>

                                        <div>
                                            <span>Color</span>
                                            <strong>
                                                {item.color}
                                            </strong>
                                        </div>

                                    </div>

                                    <div className="quote-status-mobile-order">

                                        <span>Change Order</span>

                                        <div>
                                            <button
                                                disabled={
                                                    item.order ===
                                                    1
                                                }
                                                onClick={() =>
                                                    moveStatus(
                                                        item.id,
                                                        "up"
                                                    )
                                                }
                                            >
                                                <ArrowUp size={14} />
                                            </button>

                                            <strong>
                                                {item.order}
                                            </strong>

                                            <button
                                                disabled={
                                                    item.order ===
                                                    quoteStatuses.length
                                                }
                                                onClick={() =>
                                                    moveStatus(
                                                        item.id,
                                                        "down"
                                                    )
                                                }
                                            >
                                                <ArrowDown size={14} />
                                            </button>
                                        </div>

                                    </div>

                                    <div className="quote-status-mobile-actions">

                                        <button
                                            className="quote-status-mobile-edit"
                                            onClick={() =>
                                                handleEdit(item)
                                            }
                                        >
                                            <Edit size={16} />
                                            Edit
                                        </button>

                                        <button
                                            className="quote-status-mobile-delete"
                                            onClick={() =>
                                                handleDelete(
                                                    item.id
                                                )
                                            }
                                        >
                                            <Trash2 size={16} />
                                            Delete
                                        </button>

                                    </div>

                                </div>
                            )
                        )
                    ) : (
                        <div className="quote-status-mobile-empty">
                            No quote statuses found.
                        </div>
                    )}

                </div>

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="quote-status-pagination">

                        <span>
                            Showing{" "}
                            {Math.min(
                                (page - 1) *
                                    rowsPerPage +
                                    1,
                                filteredStatuses.length
                            )}{" "}
                            to{" "}
                            {Math.min(
                                page * rowsPerPage,
                                filteredStatuses.length
                            )}{" "}
                            of {filteredStatuses.length}
                        </span>

                        <div className="quote-status-pagination-buttons">

                            <button
                                disabled={page === 1}
                                onClick={() =>
                                    setPage(
                                        (prev) => prev - 1
                                    )
                                }
                            >
                                Previous
                            </button>

                            {Array.from(
                                {
                                    length: totalPages,
                                },
                                (_, index) =>
                                    index + 1
                            ).map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    className={
                                        page === pageNumber
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setPage(pageNumber)
                                    }
                                >
                                    {pageNumber}
                                </button>
                            ))}

                            <button
                                disabled={
                                    page === totalPages
                                }
                                onClick={() =>
                                    setPage(
                                        (prev) => prev + 1
                                    )
                                }
                            >
                                Next
                            </button>

                        </div>

                    </div>
                )}

            </div>

        </div>
    );
}